// ==================== TYPES ====================

export interface TableColumn {
    key: string
    label: string
    className?: string
  }
  
  export interface TableRow {
    [key: string]: React.ReactNode
  }
  
  export type ApplicationStatus = 'Manager Accepted' | 'Manager Rejected'
  
  // ==================== PURE UTILITY FUNCTIONS ====================
  
  /**
   * Extracts error message from API response
   * @param payload - Error response payload
   * @param fallbackMessage - Message if extraction fails
   * @returns Extracted error message string
   */
  export const extractErrorMessage = (payload: any, fallbackMessage: string): string => {
    if (typeof payload === 'string') {
      return payload
    }
    return (payload)?.message || fallbackMessage
  }
  
  /**
   * Determines next page after removing an application
   * @param currentApplicationsCount - Current count of applications
   * @param currentPage - Current page number
   * @returns Next page to navigate to
   */
  export const calculateNextPage = (
    currentApplicationsCount: number,
    currentPage: number
  ): number => {
    return currentApplicationsCount === 1 ? 1 : currentPage
  }
  
  /**
   * Creates a generic note state update function
   * @param prevNotes - Previous notes state
   * @param appId - Application ID
   * @param noteText - Note text to save
   * @returns Updated notes object
   */
  export const updateNoteState = (
    prevNotes: Record<string, string>,
    appId: string,
    noteText: string
  ): Record<string, string> => {
    return {
      ...prevNotes,
      [appId]: noteText,
    }
  }
  
  /**
   * Removes a note from saved notes
   * @param prevNotes - Previous notes state
   * @param appId - Application ID to remove
   * @returns Updated notes object without the note
   */
  export const removeNoteState = (
    prevNotes: Record<string, string>,
    appId: string
  ): Record<string, string> => {
    const updated = { ...prevNotes }
    delete updated[appId]
    return updated
  }
  
  /**
   * Gets or initializes note text for an app
   * @param savedNotes - All saved notes
   * @param appId - Application ID
   * @returns Note text or empty string
   */
  export const getNoteText = (savedNotes: Record<string, string>, appId: string): string => {
    return savedNotes[appId] || ''
  }